'use client';

import { useEffect } from 'react';

import { HUBSPOT_PORTAL_ID, IS_DEPLOY_PRODUCTION } from '@/common/constants';

const HUBSPOT_SCRIPT_SRC = `//js-eu1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
const HUBSPOT_SCRIPT_ID = 'hs-script-loader';

type HubSpotQueue = unknown[][];

function getHubSpotQueue(): HubSpotQueue {
  const w = window as Window & { _hsq?: HubSpotQueue };
  w._hsq = w._hsq ?? [];
  return w._hsq;
}

function isHubSpotLoaded(): boolean {
  return document.getElementById(HUBSPOT_SCRIPT_ID) !== null;
}

// HubSpot cookies set to the exact site host (e.g. `bazak.ai`) instead of the
// default `.bazak.ai` parent domain, so they don't leak into os.bazak.ai or the
// tenant subdomains.
function primeHubSpotQueue() {
  getHubSpotQueue().push(['setCookieDomain', window.location.hostname]);
}

function loadHubSpot() {
  if (isHubSpotLoaded()) return;
  primeHubSpotQueue();
  const script = document.createElement('script');
  script.id = HUBSPOT_SCRIPT_ID;
  script.src = HUBSPOT_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Loads HubSpot at render, and deliberately not later.
 *
 * The script is what sets `hubspotutk`, and `api/scan-report` forwards that cookie to the Forms
 * API as `context.hutk` — without it a lead arrives with no source attribution, as if the visitor
 * came by direct traffic. Deferring to idle or to `load` means a first-time visitor who submits
 * the hero scan quickly has no cookie yet, and the scan field is the first thing on the page. It
 * cost real attribution and bought nothing measurable: the paint it was meant to protect happens
 * after `load` anyway, held up by script evaluation rather than by this.
 *
 * `async` + `defer` is the loader HubSpot documents. Neither blocks parsing or rendering, so there
 * is nothing here to defer further.
 */
export function HubSpotLoader() {
  useEffect(() => {
    if (!IS_DEPLOY_PRODUCTION) return;
    loadHubSpot();
  }, []);
  return null;
}
